import { useState, useEffect } from 'react';
import {
    Leaf,
    Clock,
    Truck,
    AlertCircle,
    ArrowUpRight,
    ArrowDownRight,
    Download,
    Inbox,
    CloudLightning,
} from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { OperationsFeatureGate, isOperationsTierUser } from '../components/entitlements/OperationsFeatureGate';
import { api } from '../lib/api';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { EnvironmentalImpactExport } from '../components/reports/EnvironmentalImpactExport';

interface KpiCardProps {
    title: string;
    value: string;
    trend: string;
    trendDir: 'up' | 'down';
    trendLabel: string;
    icon: React.ReactNode;
    tooltip?: string;
}

interface EmissionsResponse {
    totalIdleMinutes: number;
    totalAppointments: number;
    avgIdleMinutesPerTruck: number;
    estimatedCO2Kg: number;
    co2ChangeVsPreviousPeriodKg: number;
    co2ChangeVsPreviousPeriodPercent: number | null;
    dailyTrend: {
        date: string;
        idleMinutes: number;
        co2Kg: number;
    }[];
}

export function EnvironmentalImpact() {
    const [timeRange, setTimeRange] = useState('7d');
    const [data, setData] = useState<EmissionsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showExport, setShowExport] = useState(false);
    const [warehouseName, setWarehouseName] = useState<string>('Warehouse');
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    // companyName is a fallback; real name comes from /api/warehouses
    const facilityName = warehouseName || user?.companyName || 'Warehouse';

    // Compute explicit calendar date range for the export document
    const rangeDays = timeRange === '30d' ? 30 : timeRange === '14d' ? 14 : 7;
    const rangeLabel = timeRange === '30d' ? 'Last 30 Days' : timeRange === '14d' ? 'Last 14 Days' : 'Last 7 Days';
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - rangeDays + 1);
    startDate.setHours(0, 0, 0, 0);

    useEffect(() => {
        fetchData();
    }, [timeRange, token]);

    // Fetch warehouse name once on mount (same pattern as MasterBOLModal)
    useEffect(() => {
        if (!token || !isOperationsEntitled) return;
        api.getWarehouses()
            .then(res => {
                const name = res.warehouses?.[0]?.name;
                if (name) setWarehouseName(name);
            })
            .catch(() => { /* keep default 'Warehouse' */ });
    }, [token]);

    const fetchData = async () => {
        if (!token || !isOperationsEntitled) return;
        setIsLoading(true);
        setError(null);

        try {
            const rangeDays = timeRange === '30d' ? 30 : timeRange === '14d' ? 14 : 7;
            const response = await fetch(
                `/api/analytics/emissions?range=${rangeDays}&_ts=${Date.now()}`,
                {
                    cache: 'no-store' as RequestCache,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Cache-Control': 'no-cache',
                    },
                },
            );

            if (response.status === 304) {
                throw new Error('Stale cache (304) — please retry');
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const json: EmissionsResponse = await response.json();
            setData(json);
        } catch (err: any) {
            console.error('Failed to load emissions analytics', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOperationsEntitled) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
                <OperationsFeatureGate
                    featureName="Environmental Impact & Emissions Reporting"
                    description="Idle diesel reduction metrics, CO2 emissions avoided, and printable sustainability impact reports are included in the BayWhiz Operations plan ($199 USD/month per facility). Your Starter plan ($99 USD/month) includes core dock scheduling, gate execution, and schedule CSV exports."
                    highlights={[
                        'Yard idle-time and diesel consumption reduction estimates',
                        'CO2 emissions avoided and sustainability equivalency metrics',
                        'Printable facility environmental impact summaries',
                    ]}
                    testId="environmental-impact-operations-gate"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col pt-12 items-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{error}</p>
                    <button onClick={fetchData} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading || !data) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col pt-12 items-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-800 border-t-emerald-500 rounded-full animate-spin"></div>
                    <p className="text-slate-500 dark:text-slate-400 font-mono text-sm tracking-widest uppercase">
                        Analyzing Environmental Impact...
                    </p>
                </div>
            </div>
        );
    }

    const hasData = data && data.totalAppointments > 0;

    return (
        <>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 p-6 lg:p-8 font-sans">

            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-3">
                        Environmental Impact
                        <span className="px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 text-xs font-mono font-bold">
                            Beta
                        </span>
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Track estimated CO₂ emissions from dock idling and wait times
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                        {['7d', '14d', '30d'].map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${timeRange === range
                                    ? 'bg-emerald-600 text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                {range === '7d' ? '7 Days' : range === '14d' ? '14 Days' : '30 Days'}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => hasData && setShowExport(true)}
                        disabled={!hasData}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-slate-50 text-white dark:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Report</span>
                    </button>
                </div>
            </header>

            {!hasData ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <Inbox className="w-12 h-12 text-slate-300 dark:text-slate-700 mb-4" />
                    <h3 className="text-lg font-bold text-slate-700 dark:text-slate-300">No Data Available</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium max-w-sm mt-1">
                        No completed appointment lifecycle data available for the selected range.
                    </p>
                </div>
            ) : (
                <>
                    {/* KPIs — 2 col mobile, 3 col md, 5 col xl */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-8">
                        <KpiCard
                            title="Estimated Idle-Related CO₂ Impact"
                            value={`${data.estimatedCO2Kg} kg`}
                            trend=""
                            trendDir="up"
                            trendLabel="total this period"
                            icon={<Leaf className="w-5 h-5 text-emerald-500" />}
                        />
                        <KpiCard
                            title="Estimated CO₂ Change"
                            value={data.co2ChangeVsPreviousPeriodPercent === null ? '—' : `${Math.abs(data.co2ChangeVsPreviousPeriodKg)} kg`}
                            trend={data.co2ChangeVsPreviousPeriodPercent === null
                                ? ''
                                : data.co2ChangeVsPreviousPeriodKg <= 0 ? 'Reduced' : 'Increased'
                            }
                            trendDir={data.co2ChangeVsPreviousPeriodKg <= 0 ? 'down' : 'up'}
                            trendLabel={data.co2ChangeVsPreviousPeriodPercent === null
                                ? 'No prior-period baseline available'
                                : `(${Math.abs(data.co2ChangeVsPreviousPeriodPercent)}%) vs previous period`
                            }
                            icon={<CloudLightning className="w-5 h-5 text-amber-500" />}
                        />
                        <KpiCard
                            title="Average Idle Minutes per Truck"
                            value={`${data.avgIdleMinutesPerTruck}m`}
                            trend=""
                            trendDir="down"
                            trendLabel="wait + dock time"
                            icon={<Clock className="w-5 h-5 text-blue-500" />}
                        />
                        <KpiCard
                            title="Appointments Processed"
                            value={`${data.totalAppointments}`}
                            trend=""
                            trendDir="up"
                            trendLabel="in selected range"
                            icon={<Truck className="w-5 h-5 text-slate-500" />}
                        />
                        <KpiCard
                            title="CO₂ per Truck Visit"
                            value={data.totalAppointments === 0 ? '0 kg' : `${(Math.round((data.estimatedCO2Kg / data.totalAppointments) * 10) / 10)} kg`}
                            trend=""
                            trendDir="down"
                            trendLabel="normalized efficiency"
                            tooltip="Average estimated idle-related CO₂ impact per completed appointment lifecycle."
                            icon={<Leaf className="w-5 h-5 text-teal-500" />}
                        />
                    </div>

                    {/* Chart Section */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Estimated Idle-Related CO₂ Impact</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Daily estimated kg CO₂ from truck idle time</p>
                            </div>
                            <div className="flex gap-2">
                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <span className="w-3 h-3 bg-emerald-500 rounded-sm"></span> kg CO₂
                                </div>
                            </div>
                        </div>
                        <div className="h-[400px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={data.dailyTrend.map(t => ({ date: t.date.slice(5), co2Kg: t.co2Kg }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="co2Gradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-700/50" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} unit=" kg" />
                                    <Tooltip
                                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 13, color: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,.3)' }}
                                        itemStyle={{ color: '#10b981' }}
                                        labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
                                        formatter={(value: number | undefined) => [`${value ?? 0} kg CO₂`, 'Emissions']}
                                    />
                                    <Area type="monotone" dataKey="co2Kg" stroke="#10b981" strokeWidth={3} fill="url(#co2Gradient)" dot={false} activeDot={{ r: 5, strokeWidth: 2, fill: '#10b981' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Methodology Disclosure — grant credibility */}
                    <p className="mt-4 text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed max-w-3xl">
                        Estimates are based on recorded appointment lifecycle timestamps and a standard heavy-duty diesel idle emissions factor (EPA baseline). Values represent directional operational impact rather than vehicle-specific measurements.
                    </p>
                </>
            )}
        </div>

        {/* Export modal — mounts only when triggered */}
        {showExport && data && (
            <EnvironmentalImpactExport
                estimatedCO2Kg={data.estimatedCO2Kg}
                co2ChangeVsPreviousPeriodKg={data.co2ChangeVsPreviousPeriodKg}
                co2ChangeVsPreviousPeriodPercent={data.co2ChangeVsPreviousPeriodPercent}
                avgIdleMinutesPerTruck={data.avgIdleMinutesPerTruck}
                totalAppointments={data.totalAppointments}
                totalIdleMinutes={data.totalIdleMinutes}
                range={timeRange}
                rangeLabel={rangeLabel}
                startDate={startDate}
                endDate={endDate}
                facilityName={facilityName}
                onClose={() => setShowExport(false)}
            />
        )}
        </>
    );
}

// Solid KPI Card (No opacity, no blur)
function KpiCard({ title, value, trend, trendDir, trendLabel, icon, tooltip }: KpiCardProps) {
    const isBad = trendDir === 'up' && title.includes('CO₂') && !title.includes('Change');
    const isGood = !isBad && trend !== '';

    return (
        <div
            className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors group"
            title={tooltip}
        >
            <div className="flex justify-between items-start mb-2">
                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
                <span className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                    {icon}
                </span>
            </div>
            <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">{value}</div>
                <div className="flex items-center gap-2 mt-1">
                    {trend && (
                        <span className={`text-xs font-bold flex items-center ${isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                            {trendDir === 'up' ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                            {trend}
                        </span>
                    )}
                    <span className="text-xs text-slate-400">{trendLabel}</span>
                </div>
            </div>
        </div>
    );
}
